import { ALERT_TYPES } from 'my-purchases/constants/alertTypes'
import { ORDER_STATUS } from 'my-purchases/constants/orderStatus'
import { ORDER_TYPES } from 'my-purchases/constants/orderTypes'
import type { PAYMENT_TYPES } from 'my-purchases/constants/paymentTypes'
import { PRODUCT_STATUS } from 'my-purchases/constants/productStatus'
import type { OrderDetails } from 'my-purchases/types/purchaseDetail'
import type { CardStatus, Order } from 'my-purchases/types/purchaseList'
import type { OrderStatusDetail } from 'my-purchases/types/purchaseStatus'

import { getAlertLabel } from './getAlertLabel'
import { getDeliveryAndExpiredLabel } from './getDeliveryAndExpiredLabel'
import { getOutOfStockLabel } from './getOutOfStockLabel'
import { getStatusLabels } from './getStatusLabels'
import { countItemsByStatus } from './product'

interface GetOrderStatusInfoProps {
	order: Order | OrderDetails | OrderStatusDetail
	paymentMethod?: PAYMENT_TYPES
	isActive: boolean
	showDescription?: boolean
}

export function getOrderStatusInfo({
	order,
	paymentMethod,
	isActive,
	showDescription = false,
}: GetOrderStatusInfoProps): CardStatus | null {
	const { alert, lines, status } = order

	const type = 'type' in order ? order.type : ORDER_TYPES.ONLINE
	const outOfStockItems = 'outOfStockItems' in order ? order.outOfStockItems : 0
	const returnedItemsCount =
		'returnedItemsCount' in order ? order.returnedItemsCount : 0

	const { desktopDetails } = getStatusLabels({
		deliveryMethod:
			'deliveryMethod' in order ? order.deliveryMethod : undefined,
		paymentMethod,
		status,
	})

	const hasAlert =
		!!alert &&
		Object.keys(ALERT_TYPES).some(
			(alertType) => alertType.toLocaleLowerCase() === alert.toLocaleLowerCase()
		)

	const itemsReturned = lines
		? countItemsByStatus(lines, PRODUCT_STATUS.RETURNED)
		: returnedItemsCount

	if (hasAlert) {
		return getAlertLabel({ showDescription, alert })
	}

	if (itemsReturned) {
		return {
			statusMessage: 'myPurchases.online.details.returnedItems.subtitle',
		}
	}

	if (status === ORDER_STATUS.in_preparation) {
		const outOfStockLabel = getOutOfStockLabel({
			lines,
			outOfStockItems: outOfStockItems ?? 0,
		})

		if (outOfStockLabel) {
			return outOfStockLabel
		}
	}

	if (lines) {
		return getDeliveryAndExpiredLabel({
			desktopDetails,
			type,
			isActive,
			status,
		})
	}

	return null
}
